import { useAuth } from 'modules/auth/auth.context';
import { Card } from 'modules/shared/components';
import { TASK_STATUS } from 'modules/work-implementation/constants';
import { updateTaskStatus } from 'modules/work-implementation/projects-list/service';
import {
  accumulateTimeUsed,
  isTaskRunning,
  isTaskTerminal,
} from 'modules/work-implementation/projects-list/utils';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import logger, { logError } from 'utils/logger';
import { CommonDetails, TaskViewCard } from './components';
import { submitEmployeeTask } from '../service';
import { useEmployeeProjectView } from './hooks/useEmployeeProjectView';

export default function EmployeeProjectView() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const employeeId = user?.employee_id;

  const { projectData, loading, refresh } = useEmployeeProjectView(employeeId, projectId);

  const handleTaskToggle = async (projectId, task) => {
    const now = new Date();

    if (!task || isTaskTerminal(task.currentWorkingStatus)) return;

    const taskId = task.id;
    const isRunning = isTaskRunning(task);

    const payload = isRunning
      ? {
          intent: 'PAUSE',
          taskId,
          currentWorkingStatus: TASK_STATUS.IN_PROGRESS,
          timeUsed: accumulateTimeUsed(task.timeUsed, task.lastPlay, now),
          lastPlay: null,
        }
      : {
          intent: 'PLAY',
          taskId,
          currentWorkingStatus: TASK_STATUS.IN_PROGRESS,
          lastPlay: now.toISOString(),
        };

    try {
      await updateTaskStatus(employeeId, taskId, payload);
    } catch (error) {
      toast.error(
        logger.error({
          message: 'Failed to update task working state',
          error,
          payload,
        }),
      );
    }
  };

  const handleTaskSubmit = async (task, data) => {
    try {
      await submitEmployeeTask(employeeId, task.id, data);
      toast.success('File submitted');
      refresh();
    } catch (error) {
      toast.error(
        logError({ message: 'Failed to submit file', error, code: 'EMPLOYEE_TASK_SUBMIT_FAILED' }),
      );
    }
  };

  if (!loading && !projectData) {
    return (
      <Card>
        <span>None of this project&apos;s tasks are assigned to you, so there is nothing to show here.</span>
      </Card>
    );
  }

  return (
    <>
      <CommonDetails loading={loading} projectData={projectData?.projectDetails} />

      <div className='column gap-3'>
        {projectData?.tasks.map((task) => (
          <TaskViewCard
            key={task.id}
            task={task}
            projectData={projectData?.projectDetails}
            onTaskToggle={handleTaskToggle}
            onTaskSubmit={handleTaskSubmit}
          />
        ))}
      </div>
    </>
  );
}
